import { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useQuery } from "@tanstack/react-query"
import { AxiosRequestConfig } from "axios"
import apiClient from "./apiClient"

/**
 * One accepted-format list per upload destination, served by the backend
 * (`GET /upload-formats`, authored in `services/upload_formats.py`).
 *
 * Every picker used to hard-code its own `accept` string, and four of them
 * disagreed with the API they posted to — the customer-request picker offered
 * `.xls` the API 415s, the knowledge base blocked `.md` the API accepts. The
 * chat makes that drift user-visible in a new way: the agent PROPOSES a
 * destination, so an accepted-here/refused-there pair becomes the agent offering
 * something that then fails. So the backend list is the only list.
 *
 * FALLBACKS BELOW ARE A LAST RESORT, not a second source of truth: they only
 * apply while the request is in flight or after it failed, and they must be kept
 * equal to the backend. Anything reading them is displaying a picker before the
 * fetch resolved — the API still decides.
 */
export type UploadDestination =
  | "knowledge_base"
  | "questionnaire"
  | "master_data_import"
  | "supplier_declaration"
  | "customer_request"

/**
 * Translation key for each destination's human-facing name. The wire value
 * (`"supplier_declaration"`) is an internal slug shared with the backend's
 * `UploadDestination` — never meant for display. `destinationDisplayName`
 * below looks this up instead of interpolating the slug directly, in both
 * the route_file confirm card and the "Filed to X" chip — both used to show
 * the raw slug verbatim.
 */
const DESTINATION_LABEL_KEYS: Record<UploadDestination, TranslationKeys> = {
  knowledge_base: "AI_CHAT/DESTINATION/KNOWLEDGE_BASE",
  questionnaire: "AI_CHAT/DESTINATION/QUESTIONNAIRE",
  master_data_import: "AI_CHAT/DESTINATION/MASTER_DATA_IMPORT",
  supplier_declaration: "AI_CHAT/DESTINATION/SUPPLIER_DECLARATION",
  customer_request: "AI_CHAT/DESTINATION/CUSTOMER_REQUEST",
}

function isUploadDestination(
  destination: string,
): destination is UploadDestination {
  return destination in DESTINATION_LABEL_KEYS
}

/**
 * The destination's translated display name, or the slug itself as a last
 * resort — a slug the backend added and the frontend hasn't caught up to yet
 * should still render as SOMETHING recognizable rather than crash. Takes
 * `translate` as a param instead of exporting a key for the caller to
 * translate itself, so an unrecognized slug never has to be forced through
 * `TranslationKeys` with a type assertion.
 */
export function destinationDisplayName(
  destination: string,
  translate: (key: TranslationKeys) => string,
): string {
  return isUploadDestination(destination)
    ? translate(DESTINATION_LABEL_KEYS[destination])
    : destination
}

export interface DestinationFormats {
  extensions: string[]
  contentTypes: string[]
}

export interface UploadFormats {
  destinations: Record<UploadDestination, DestinationFormats>
  /**
   * Formats refused somewhere, mapped to the sentence naming the fix — not a
   * claim that the format is refused everywhere. Check `destinations` for what
   * a specific destination actually accepts.
   */
  declined: Record<string, string>
}

interface UploadFormatsApiResponse {
  destinations: Record<
    string,
    { extensions: string[]; content_types: string[] }
  >
  declined: Record<string, string>
}

/**
 * Mirrors `services/upload_formats.py`. `.xls` is destination-specific, not a
 * product-wide switch: `master_data_import` accepts it (pre-existing
 * behaviour, unchanged); `questionnaire`, `customer_request` and
 * `supplier_declaration` all refuse it (`questionnaire` by a deliberate
 * 2026-08-20 decision, the other two never took it).
 */
export const FALLBACK_UPLOAD_FORMATS: UploadFormats = {
  destinations: {
    knowledge_base: {
      extensions: [
        ".pdf",
        ".docx",
        ".doc",
        ".txt",
        ".md",
        ".pptx",
        ".csv",
        ".xlsx",
        ".xlsm",
      ],
      contentTypes: [],
    },
    questionnaire: {
      extensions: [".xlsx", ".pdf"],
      contentTypes: [],
    },
    master_data_import: {
      extensions: [".xlsx", ".xlsm", ".csv", ".xls"],
      contentTypes: [],
    },
    supplier_declaration: {
      extensions: [".pdf", ".xlsx", ".xlsm"],
      contentTypes: [],
    },
    customer_request: {
      extensions: [".xlsx", ".pdf", ".csv"],
      contentTypes: [],
    },
  },
  declined: {
    ".xls":
      "Legacy Excel files (.xls) are not supported here. Open it in Excel and save it as .xlsx, then upload again.",
  },
}

export const UPLOAD_FORMATS_QUERY_KEY = ["uploadFormats"]

export const uploadFormatsApiCalls = {
  getUploadFormats: async (): Promise<UploadFormats> => {
    // skipCaseConversion: the destination slugs ("knowledge_base",
    // "master_data_import", ...) are DATA living in object keys, not schema
    // field names — apiClient's snake->camel pass would rewrite them to
    // "knowledgeBase" etc. and "content_types" to "contentTypes", and every
    // lookup below keys on the raw snake_case slug. Same pattern as
    // declarationsApi.ts's read-model fetch.
    const response = await apiClient.get<UploadFormatsApiResponse>(
      "/upload-formats",
      { skipCaseConversion: true } as AxiosRequestConfig & {
        skipCaseConversion?: boolean
      },
    )
    const destinations = Object.fromEntries(
      Object.entries(response.data.destinations).map(([slug, formats]) => [
        slug,
        { extensions: formats.extensions, contentTypes: formats.content_types },
      ]),
    ) as Record<UploadDestination, DestinationFormats>
    return { destinations, declined: response.data.declined }
  },
}

/**
 * The list is identical for every org and changes only on deploy, so it is
 * cached for the session rather than refetched per picker mount.
 */
export const useUploadFormats = () => {
  const query = useQuery({
    queryKey: UPLOAD_FORMATS_QUERY_KEY,
    queryFn: uploadFormatsApiCalls.getUploadFormats,
    staleTime: Infinity,
    gcTime: Infinity,
  })
  return query.data ?? FALLBACK_UPLOAD_FORMATS
}

/** Extensions accepted at one destination, ready for an `accept` attribute. */
export function acceptAttributeFor(
  formats: UploadFormats,
  destination: UploadDestination,
): string {
  return formats.destinations[destination].extensions.join(",")
}

export function extensionOf(fileName: string): string {
  if (!fileName.includes(".")) return ""
  return "." + (fileName.split(".").pop() ?? "").toLowerCase()
}

/**
 * Why this file is refused here, or null if it is accepted. Advisory — the API
 * decides from the bytes; this exists to spare an obvious mistake a round-trip,
 * and its message names the fix so the user learns the rule from the refusal.
 */
export function rejectionReason(
  formats: UploadFormats,
  destination: UploadDestination,
  fileName: string,
): string | null {
  const extension = extensionOf(fileName)
  if (formats.destinations[destination].extensions.includes(extension)) {
    return null
  }
  if (formats.declined[extension]) {
    return formats.declined[extension]
  }
  // Mirrors the backend's refusal_message() wording exactly (upload_formats.py):
  // `"{extension}" files are not accepted here...` / `Files without an
  // extension are not accepted here...` — this is the fallback client-side
  // check, so it must read like the same refusal, not a different one.
  const accepted = formats.destinations[destination].extensions.join(", ")
  const what = extension
    ? `"${extension}" files are`
    : "Files without an extension are"
  return `${what} not accepted here. Supported: ${accepted}.`
}

/**
 * `data_room`'s accepted-formats list, read with the one cast this needs.
 * `data_room` is deliberately excluded from `UploadDestination` (it is not
 * chat-routable — see `CHAT_EXCLUDED_DESTINATIONS` below), so it is not a key
 * `formats.destinations` is typed to allow — but the backend still serves its
 * list under that slug, and the data room page needs it for its own
 * accepted-formats hint and its file input's `accept` attribute. Falls back
 * to an empty list (never undefined) so callers can render unconditionally
 * while `/upload-formats` is still in flight — `FALLBACK_UPLOAD_FORMATS`
 * predates `data_room` and has no entry for it either.
 */
export function dataRoomFormats(formats: UploadFormats): DestinationFormats {
  return (
    (formats.destinations as Record<string, DestinationFormats>).data_room ?? {
      extensions: [],
      contentTypes: [],
    }
  )
}

/**
 * Destinations that exist in the `/upload-formats` payload but are not
 * reachable through chat. Mirrors the backend's `_CHAT_ROUTABLE_DESTINATIONS`
 * exclusion in `services/upload_formats.py`: `data_room` files arrive through
 * the data room's own surfaces (the guided upload flow, drag-and-drop onto its
 * tree), never via chat, so its formats (zip, images) must not leak into the
 * composer's `accept` attribute or its routing candidates. Every helper below
 * that unions or filters over the FULL destination set reads this first.
 */
const CHAT_EXCLUDED_DESTINATIONS = new Set<string>(["data_room"])

/**
 * Every extension the chat composer may attach: the union across every
 * chat-routable destination (currently five — see `CHAT_EXCLUDED_DESTINATIONS`
 * for what's deliberately left out). A file is attached before any destination
 * is chosen — often with no destination ever chosen, because a file that is
 * never routed IS the thread-context file — so the composer's gate is the
 * union, and narrowing to one destination happens at routing time.
 */
export function chatAttachAccept(formats: UploadFormats): string {
  const union = new Set<string>()
  for (const [slug, destination] of Object.entries(formats.destinations)) {
    if (CHAT_EXCLUDED_DESTINATIONS.has(slug)) continue
    for (const extension of destination.extensions) union.add(extension)
  }
  return [...union].join(",")
}

/**
 * Which chat-routable destinations accept this file, in list order — the
 * deterministic first half of the routing decision. A single candidate means
 * no classification is needed; an empty result means the file is not routable
 * anywhere and the chat should say so rather than offer a list. Destinations in
 * `CHAT_EXCLUDED_DESTINATIONS` (e.g. `data_room`) are never returned, even when
 * they accept the extension — chat cannot route there.
 */
export function destinationsForFile(
  formats: UploadFormats,
  fileName: string,
): UploadDestination[] {
  const extension = extensionOf(fileName)
  return (Object.keys(formats.destinations) as UploadDestination[]).filter(
    (destination) =>
      !CHAT_EXCLUDED_DESTINATIONS.has(destination) &&
      formats.destinations[destination].extensions.includes(extension),
  )
}
